import type React from 'react';

import { Grid } from '@change/ds-layout';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import { HeaderImage } from 'src/app/pages/starterDashboardV2/shared/components';

import { PageHeaderTitle } from './components/PageHeaderTitle';
import { PageHeaderToolbar } from './components/PageHeaderToolbar';
import { usePageHeader } from './hooks/usePageHeader';
import { container as containerStyles, header as headerStyles } from './PageHeader.styles';

type PageHeaderProps = {
	/**
	 * When true, hides the sticky header on desktop (lg and xl breakpoints).
	 * Used to hide the sticky header when the inline header is visible in PageBody.
	 */
	hideOnDesktop?: boolean;
};

export const PageHeader: React.FC<PageHeaderProps> = ({ hideOnDesktop = false }) => {
	const {
		data: { mobile, orientation, actionButtons, buttonVariant, imageUrl, isImageProcessing, title },
	} = usePageHeader();
	const { translate } = useI18n();
	const headerStyle = headerStyles({ isMobile: mobile, orientation });
	const containerStyle = containerStyles({ isMobile: mobile, orientation });

	// Conditionally make component invisible on desktop when inline header is visible
	const visibilityClasses = hideOnDesktop ? 'lg:invisible lg:pointer-events-none' : '';

	return (
		<section
			className={cn(
				'border-stroke-weaker bg-background-base z-20 w-full border-b md:sticky md:top-0 md:right-0 md:left-0',
				visibilityClasses,
			)}
		>
			<Grid className="px-0! py-0!">
				<header
					className={headerStyle}
					aria-label={translate('corgi.petition.starter_dashboard_v2.page_header.aria_label')}
					data-testid="page-header"
				>
					<div data-testid="page-header-container" className={containerStyle}>
						<PageHeaderTitle
							image={
								<HeaderImage
									imageUrl={imageUrl}
									isImageProcessing={isImageProcessing}
									alt={title}
									imageClassName="rounded-medium h-10 w-10 gap-3.5"
									processingClassName="rounded-medium h-10 w-10"
									testIdPrefix="starter-dashboard-header"
								/>
							}
							title={title}
						/>
						{actionButtons.length > 0 && (
							<PageHeaderToolbar actionButtons={actionButtons} mobile={mobile} buttonVariant={buttonVariant} />
						)}
					</div>
				</header>
			</Grid>
		</section>
	);
};
